import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { parseResponseSafely, getFriendlyPaymentError } from "../lib/utils";
import {
  ArrowLeft,
  SpinnerGap,
  CreditCard,
  Receipt,
  CheckCircle,
  XCircle,
  Clock,
  CaretRight,
  CaretDown,
  CaretUp,
  Plus,
  Trash,
  Star,
  Lock,
  ShieldCheck,
  ArrowSquareOut,
  CalendarBlank,
  CurrencyDollar,
  ArrowsClockwise,
  ArrowClockwise,
  ArrowsCounterClockwise,
  Info,
  Bug,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Finix.js SDK URL
const FINIX_JS_URL = "https://js.finix.com/v/2/finix.js";

// Timeout for form initialization (ms)
const FORM_INIT_TIMEOUT = 10000;

/**
 * BillingPage - Free Service Pro Billing
 * 
 * SECURITY: All card entry occurs on processor-hosted pages.
 * This application NEVER accepts, transmits, or stores raw card data.
 * Only tokenized payment method IDs are stored.
 * PCI exposure is SAQ-A only.
 */
export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const tenantIdRef = useRef(null);
  
  // Keep tenantIdRef in sync with user
  useEffect(() => {
    tenantIdRef.current = user?.tenant_id || null;
  }, [user]);
  
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [billingStatus, setBillingStatus] = useState(null);
  
  // Payment mode info (test/production)
  const [modeInfo, setModeInfo] = useState({ mode: "test", hosted_only: true });
  
  // Billing policy state
  const [billingPolicy, setBillingPolicy] = useState({
    billing_required: false,
    can_delete_payment_method: true,
    payment_method_count: 0,
    policy_message: "",
  });
  
  // Modal state
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [replaceCardModalOpen, setReplaceCardModalOpen] = useState(false);
  const [transactionDetailModal, setTransactionDetailModal] = useState(null);
  
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePaymentMethodId, setDeletePaymentMethodId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // TC Premier hosted form state (legacy)
  const [registeringToken, setRegisteringToken] = useState(false);

  // Slice 3: Tokenization state
  const [tokenizationConfig, setTokenizationConfig] = useState(null);
  const [tokenizationLoading, setTokenizationLoading] = useState(false);
  const [tokenizationError, setTokenizationError] = useState(null); // Init/load errors only
  const [submitError, setSubmitError] = useState(null); // Submit/validation errors (form stays visible)
  const [finixDebugError, setFinixDebugError] = useState(null); // Preview-only: raw Finix error for debugging
  const [debugExpanded, setDebugExpanded] = useState(false); // Preview-only: toggle for debug details
  const [finixFormReady, setFinixFormReady] = useState(false);
  const [finixSubmitting, setFinixSubmitting] = useState(false);
  const finixFormRef = useRef(null);
  const finixPaymentFormInstance = useRef(null);
  const finixTimeoutRef = useRef(null);
  const attachInProgressRef = useRef(false); // Idempotency guard for backend attach
  
  // Environment check for detailed logging (preview/staging only)
  const isPreviewEnv = process.env.NODE_ENV !== 'production' || 
    window.location.hostname.includes('preview') || 
    window.location.hostname.includes('staging');
  
  // Helper: Extract safe fields from Finix error for debug display (no sensitive data)
  const extractSafeFinixError = useCallback((err, tenantId) => {
    if (!err) return null;
    const truncate = (str, max = 200) => {
      if (!str) return undefined;
      const s = String(str);
      return s.length > max ? s.substring(0, max) + '...' : s;
    };
    return {
      code: truncate(err.code),
      type: truncate(err.type),
      name: truncate(err.name),
      message: truncate(err.message),
      field: truncate(err.field),
      details: err.details ? truncate(JSON.stringify(err.details)) : undefined,
      tenantId: tenantId || 'unknown',
      ts: new Date().toISOString(),
    };
  }, []);

  // Slice 3: Payment method summary from new endpoint
  const [paymentMethodSummary, setPaymentMethodSummary] = useState(null);

  // Handle callback from payment processor redirect mode
  useEffect(() => {
    // Payment processor redirect mode returns these parameters after our return handler redirects:
    // - tc_success: "true" if card was added successfully
    // - tc_error: error type if failed
    // - tc_message: Human-readable message
    
    const tcSuccess = searchParams.get("tc_success");
    const tcError = searchParams.get("tc_error");
    const tcMessage = searchParams.get("tc_message");
    
    // Handle redirect mode response (new flow)
    if (tcSuccess === "true") {
      console.log("[Payment] Redirect success:", tcMessage);
      setSearchParams({});
      toast.success(tcMessage || "Card added successfully!");
      fetchData(); // Refresh payment methods list
      return;
    }
    
    if (tcError) {
      console.error("[Payment] Redirect error:", tcError, tcMessage);
      setSearchParams({});
      toast.error(tcMessage || `Payment failed: ${tcError}`);
      return;
    }
    
    // Legacy callback handling (popup mode - kept for backwards compatibility)
    // Payment processor returns these parameters in the query string after redirect:
    // - billingid: The tokenized card ID
    // - status: "approved" or error status
    // - Additional card info may be returned depending on processor configuration
    
    const billingId = searchParams.get("billingid");
    const status = searchParams.get("status");
    const sessionToken = searchParams.get("session_token");
    const error = searchParams.get("error");
    const offenders = searchParams.get("offenders");
    
    // Log callback params for debugging
    if (billingId || status || error) {
      console.log("[Payment Callback] Received params:", {
        billingId: billingId ? billingId.substring(0, 8) + "..." : null,
        status,
        error,
        offenders,
        sessionToken: sessionToken ? sessionToken.substring(0, 8) + "..." : null
      });
    }
    
    // Handle error responses
    if (status === "baddata" || status === "error" || error) {
      setSearchParams({});
      const errorMsg = error || `Payment form error: ${status}`;
      console.error("[Payment Callback] Error:", { status, error, offenders });
      toast.error(`${errorMsg}${offenders ? ` (${offenders})` : ""}`);
      return;
    }
    
    // Handle successful tokenization - processor returns billingid on status=approved
    if (billingId && status === "approved") {
      setSearchParams({});
      handleRegisterToken({
        billing_id: billingId,
        session_token: sessionToken,
        // Processor may return additional info - we accept what's provided
        brand: searchParams.get("brand") || "unknown",
        last4: searchParams.get("cc") || searchParams.get("last4") || "****",
        exp_month: parseInt(searchParams.get("exp_month")) || 12,
        exp_year: parseInt(searchParams.get("exp_year")) || 2030,
      });
    } else if (billingId) {
      // billingId present but status not explicitly approved - still try
      setSearchParams({});
      handleRegisterToken({
        billing_id: billingId,
        session_token: sessionToken,
        brand: searchParams.get("brand") || "unknown",
        last4: searchParams.get("cc") || searchParams.get("last4") || "****",
        exp_month: parseInt(searchParams.get("exp_month")) || 12,
        exp_year: parseInt(searchParams.get("exp_year")) || 2030,
      });
    }
  }, [searchParams]);

  // Register token from payment processor callback
  const handleRegisterToken = async (tokenData) => {
    setRegisteringToken(true);
    console.log("[Payment Register] Registering token:", {
      billing_id: tokenData.billing_id?.substring(0, 8) + "...",
      brand: tokenData.brand,
      last4: tokenData.last4
    });
    
    try {
      const response = await fetch(`${API}/billing/payment-methods/register-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(tokenData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log("[Payment Register] Success:", data);
        toast.success("Payment method added successfully!");
        fetchData();
      } else {
        console.error("[Payment Register] Failed:", data);
        toast.error(data.detail || data.error || "Failed to register payment method");
      }
    } catch (error) {
      console.error("[Payment Register] Error:", error);
      toast.error("Failed to register payment method");
    } finally {
      setRegisteringToken(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch TC mode info
      const modeRes = await fetch(`${API}/billing/test-mode-status`, { credentials: "include" });
      if (modeRes.ok) {
        const modeData = await modeRes.json();
        setModeInfo(modeData);
      }
      
      // Fetch payment methods
      const methodsRes = await fetch(`${API}/billing/payment-methods`, { credentials: "include" });
      if (methodsRes.ok) {
        const methodsData = await methodsRes.json();
        const defaultPmId = methodsData.default_payment_method?.payment_method_id;
        
        // Merge is_default flag into payment methods array based on default_payment_method
        const methodsWithDefault = (methodsData.payment_methods || []).map(pm => ({
          ...pm,
          is_default: pm.payment_method_id === defaultPmId || 
                      (methodsData.payment_methods?.length === 1)  // Single card = default
        }));
        
        setPaymentMethods(methodsWithDefault);
        setDefaultPaymentMethod(methodsData.default_payment_method);
      }
      
      // Fetch billing status
      const statusRes = await fetch(`${API}/billing/status`, { credentials: "include" });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setBillingStatus(statusData);
      }
      
      // Fetch billing policy (for replace-vs-delete UI logic)
      const policyRes = await fetch(`${API}/billing/billing-required`, { credentials: "include" });
      if (policyRes.ok) {
        const policyData = await policyRes.json();
        setBillingPolicy(policyData);
      }
      
      // Fetch transactions
      const txRes = await fetch(`${API}/billing/transactions?status=${statusFilter}&limit=20`, { credentials: "include" });
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData.transactions || []);
        setTotalTransactions(txData.total || 0);
      }
    } catch (error) {
      console.error("Error fetching billing data:", error);
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for postMessage from payment popup bridge page
  useEffect(() => {
    const handlePaymentMessage = (event) => {
      // SECURITY: Only accept messages from our own origin
      if (event.origin !== window.location.origin) {
        return;
      }

      const { type, ok, error, paymentMethodId } = event.data || {};

      if (type === "TC_CARD_SAVED") {
        console.log("[Payment] Received postMessage:", { ok, error, paymentMethodId });
        
        if (ok) {
          toast.success("Card added successfully!");
          // Refresh payment methods to show the new card
          fetchData();
        } else {
          toast.error(error || "Failed to add card. Please try again.");
        }
      }
    };

    window.addEventListener("message", handlePaymentMessage);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("message", handlePaymentMessage);
    };
  }, [fetchData]);

  const handleSetDefault = async (paymentMethodId) => {
    try {
      const response = await fetch(`${API}/billing/payment-methods/default`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ payment_method_id: paymentMethodId }),
      });
      
      if (response.ok) {
        toast.success("Default payment method updated");
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update default payment method");
      }
    } catch (error) {
      toast.error("Failed to update default payment method");
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (paymentMethodId) => {
    setDeletePaymentMethodId(paymentMethodId);
    setDeleteModalOpen(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletePaymentMethodId(null);
  };

  const handleDeletePaymentMethod = async () => {
    if (!deletePaymentMethodId) return;
    
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API}/billing/payment-methods/${deletePaymentMethodId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Payment method removed");
        closeDeleteModal();
        fetchData();
      } else {
        const errorCode = data?.detail?.error_code;
        const errorMessage = data?.detail?.message || data?.detail;
        
        // Handle specific error codes
        if (response.status === 404 && errorCode === "PAYMENT_METHOD_NOT_FOUND") {
          toast.info("That card is no longer on file. Refreshing...");
          closeDeleteModal();
          fetchData(); // Refresh to show current state
        } else if (response.status === 409 && errorCode === "BILLING_CARD_REQUIRED") {
          toast.error(errorMessage || "Cannot remove your only payment method while you have an active subscription.");
          closeDeleteModal();
        } else {
          // Generic error with friendly fallback
          toast.error(typeof errorMessage === 'string' ? errorMessage : "Unable to remove payment method. Please try again.");
          closeDeleteModal();
          fetchData(); // Always refresh on error
        }
      }
    } catch (error) {
      toast.error("Unable to remove payment method. Please try again.");
      closeDeleteModal();
      fetchData(); // Refresh on network error
    } finally {
      setDeleteLoading(false);
    }
  };

  // ==========================================================================
  // SLICE 3: Finix Tokenization Form Functions (Reliable Implementation)
  // ==========================================================================

  // Fetch payment method summary from new Slice 2 endpoint
  const fetchPaymentMethodSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API}/billing/payment-method`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentMethodSummary(data.payment_method_summary);
      }
    } catch (error) {
      console.log("[Billing] Could not fetch payment method summary:", error.message);
    }
  }, []);

  // Load Finix.js SDK dynamically with timeout
  const loadFinixSdk = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.Finix) {
        console.log("[Billing] Finix SDK already available");
        resolve();
        return;
      }
      
      const timeoutId = setTimeout(() => {
        reject(new Error("Payment form failed to load. Please check your connection and try again."));
      }, FORM_INIT_TIMEOUT);
      
      const existingScript = document.querySelector(`script[src="${FINIX_JS_URL}"]`);
      if (existingScript) {
        const handleLoad = () => { clearTimeout(timeoutId); resolve(); };
        const handleError = () => { clearTimeout(timeoutId); reject(new Error("Payment form failed to load.")); };
        existingScript.addEventListener("load", handleLoad);
        existingScript.addEventListener("error", handleError);
        return;
      }
      
      const script = document.createElement("script");
      script.src = FINIX_JS_URL;
      script.async = true;
      script.onload = () => { clearTimeout(timeoutId); resolve(); };
      script.onerror = () => { clearTimeout(timeoutId); reject(new Error("Payment form failed to load.")); };
      document.head.appendChild(script);
    });
  }, []);

  // Cleanup Finix form
  const cleanupFinixForm = useCallback(() => {
    if (finixTimeoutRef.current) {
      clearTimeout(finixTimeoutRef.current);
      finixTimeoutRef.current = null;
    }
    if (finixPaymentFormInstance.current) {
      try {
        if (typeof finixPaymentFormInstance.current.destroy === "function") {
          finixPaymentFormInstance.current.destroy();
        }
      } catch (e) { /* ignore */ }
      finixPaymentFormInstance.current = null;
    }
    if (finixFormRef.current) {
      finixFormRef.current.innerHTML = "";
    }
    setFinixFormReady(false);
    setFinixSubmitting(false);
    setSubmitError(null); // Clear submit errors on cleanup
    setFinixDebugError(null); // Clear debug error on cleanup
    setDebugExpanded(false); // Reset debug toggle
  }, []);

  // Fetch tokenization config
  const fetchTokenizationConfig = useCallback(async () => {
    setTokenizationLoading(true);
    setTokenizationError(null);
    setFinixFormReady(false);
    
    try {
      const response = await fetch(`${API}/billing/tokenization-bootstrap`, {
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (!data.ok) {
        setTokenizationError(data.message || "Payment setup is temporarily unavailable.");
        setTokenizationConfig(null);
        return;
      }
      
      setTokenizationConfig(data);
      
      if (data.provider === "finix") {
        await loadFinixSdk();
      }
      
    } catch (error) {
      console.error("[Billing] Tokenization config error:", error);
      setTokenizationError(error.message || "Unable to load payment form. Please try again.");
    } finally {
      setTokenizationLoading(false);
    }
  }, [loadFinixSdk]);

  // Initialize Finix Payment Form with onSubmit callback
  // The Finix iframe's internal submit button is the single source of truth
  const initializeFinixForm = useCallback(() => {
    if (!tokenizationConfig || tokenizationConfig.provider !== "finix") return;
    if (!window.Finix || !finixFormRef.current) return;
    if (finixPaymentFormInstance.current) return;
    
    try {
      const environment = tokenizationConfig.environment || "sandbox";
      const applicationId = tokenizationConfig.application_id;
      
      // ACH/Bank toggle - default to card-only for SaaS billing
      const allowBankAccount = tokenizationConfig.allow_bank_account === true;
      const paymentMethods = allowBankAccount ? ["card", "bank"] : ["card"];
      
      finixFormRef.current.innerHTML = "";
      
      finixTimeoutRef.current = setTimeout(() => {
        setTokenizationError("Payment form couldn't load. Please try again.");
        cleanupFinixForm();
      }, FORM_INIT_TIMEOUT);
      
      const form = window.Finix.PaymentForm(
        finixFormRef.current,
        environment,
        applicationId,
        {
          paymentMethods,
          defaultCountry: "US",
          showAddress: false,
          styles: {
            default: {
              color: "#18181b",
              fontSize: "16px",
              fontFamily: "system-ui, -apple-system, sans-serif",
              "::placeholder": { color: "#a1a1aa" },
            },
            error: { color: "#dc2626" },
          },
          // Form state update - marks form as ready and clears errors
          onUpdate: () => {
            if (!finixFormReady && finixTimeoutRef.current) {
              clearTimeout(finixTimeoutRef.current);
              finixTimeoutRef.current = null;
              setFinixFormReady(true);
            }
            // Clear any previous submit error on user input (form is being corrected)
            if (submitError) {
              setSubmitError(null);
            }
            // Clear debug error when user starts correcting
            if (finixDebugError) {
              setFinixDebugError(null);
              setDebugExpanded(false);
            }
          },
          // Tokenization callback - triggered by Finix's internal submit button
          // This is the SINGLE submission path - no external submit button
          onSubmit: async (error, response) => {
            const timestamp = new Date().toISOString();
            const tenantId = tenantIdRef.current || 'unknown';
            
            // SAFETY BOUNDARY: Wrap entire submit in try/catch/finally
            try {
              // Prevent double-submission
              if (attachInProgressRef.current) {
                console.log('[Billing] Attach already in progress, ignoring duplicate submit');
                return;
              }
              
              if (error) {
                // Extract safe debug info (no sensitive data: no card/cvv/name/address/zip values)
                const debugError = extractSafeFinixError(error, tenantId);
                
                // PREVIEW ONLY: Console log with standardized format
                if (isPreviewEnv) {
                  console.error('[FINIX_SUBMIT_ERROR]', {
                    code: debugError.code || 'undefined',
                    type: debugError.type || 'undefined',
                    name: debugError.name || 'undefined',
                    message: debugError.message || 'undefined',
                    field: debugError.field || 'undefined',
                    details: debugError.details || 'undefined',
                    tenantId: debugError.tenantId,
                    ts: debugError.ts,
                  });
                  // Store for UI display
                  setFinixDebugError(debugError);
                }
                
                // TELEMETRY: payment_token_failure (safe fields only - always logged)
                console.error('[Billing] payment_token_failure', {
                  tenant_id: tenantId,
                  timestamp,
                  error_code: error.code || 'UNKNOWN',
                  error_type: error.type || 'VALIDATION',
                });
                
                // Normalize error to human-readable message
                const errorMsg = error.message || '';
                const errorCode = (error.code || '').toUpperCase();
                let friendlyMessage = "Card validation failed. Please check your details.";
                
                // ZIP/Postal code errors
                if (errorMsg.toLowerCase().includes('postal') || 
                    errorMsg.toLowerCase().includes('zip') ||
                    errorCode.includes('POSTAL') ||
                    errorCode.includes('ZIP') ||
                    errorCode.includes('ADDRESS')) {
                  friendlyMessage = "ZIP/Postal code is required.";
                }
                // Card number errors
                else if (errorCode.includes('CARD') || errorCode.includes('NUMBER') || errorCode.includes('PAN')) {
                  friendlyMessage = "Please check your card number.";
                }
                // Expiration errors
                else if (errorCode.includes('EXPIR') || errorCode.includes('DATE')) {
                  friendlyMessage = "Please check your expiration date.";
                }
                // CVV/Security code errors
                else if (errorCode.includes('CVV') || errorCode.includes('CVC') || errorCode.includes('SECURITY')) {
                  friendlyMessage = "Please check your security code (CVV).";
                }
                // Use original message if it's already user-friendly
                else if (errorMsg && !errorMsg.includes('object') && errorMsg.length < 100) {
                  friendlyMessage = `Card validation failed: ${errorMsg}`;
                }
                
                // Set submit error (keeps form visible for retry)
                setSubmitError(friendlyMessage);
                toast.error(friendlyMessage);
                return;
              }
              
              // Clear debug error on successful tokenization
              setFinixDebugError(null);
              setDebugExpanded(false);
              
              const tokenData = response?.data || {};
              const paymentInstrumentId = tokenData.id;
              const cardData = tokenData.card || {};
              const addressData = tokenData.address || {};
              
              if (!paymentInstrumentId) {
                // TELEMETRY: payment_token_failure
                console.error('[Billing] payment_token_failure', {
                  tenant_id: tenantId,
                  timestamp,
                  error_code: 'NO_TOKEN_ID',
                });
                setSubmitError("Unable to process card. Please try again.");
                toast.error("Unable to process card. Please try again.");
                return;
              }
              
              // TELEMETRY: payment_token_success
              console.log('[Billing] payment_token_success', {
                tenant_id: tenantId,
                timestamp,
                token_prefix: paymentInstrumentId.substring(0, 8),
              });
              
              // Country validation (frontend - backend is authoritative)
              const countryRaw = addressData?.country || addressData?.address_country || "US";
              const countryUpper = countryRaw.toUpperCase().trim();
              const COUNTRY_NORMALIZE = { "US": "US", "USA": "US", "CA": "CA", "CAN": "CA" };
              const normalizedCountry = COUNTRY_NORMALIZE[countryUpper] || countryUpper;
              
              if (!["US", "CA"].includes(normalizedCountry)) {
                setSubmitError("We currently only accept payment methods from the United States and Canada.");
                toast.error("We currently only accept payment methods from the United States and Canada.");
                return;
              }
              
              // Set submitting state and idempotency guard
              setFinixSubmitting(true);
              attachInProgressRef.current = true;
              
              // Backend attach - single execution guaranteed by attachInProgressRef
              const saveResponse = await fetch(`${API}/billing/payment-method`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  payment_instrument_id: paymentInstrumentId,
                  instrument_type: "card",
                  country: normalizedCountry,
                  // Note: backend now fetches metadata from Finix, these are ignored
                  brand: cardData.brand || cardData.type || null,
                  last4: cardData.last_four || cardData.lastFour || null,
                  exp_month: cardData.expiration_month || cardData.expirationMonth || null,
                  exp_year: cardData.expiration_year || cardData.expirationYear || null,
                }),
              });
              
              // Parse response ONCE to avoid "body stream already read" error
              const parsed = await parseResponseSafely(saveResponse);
              
              if (!parsed.ok) {
                // Extract error message from parsed data with full diagnostic chain
                const detail = parsed.data?.detail || {};
                const errorMsg = detail?.message 
                  || detail?.finix_message
                  || (typeof detail === 'string' ? detail : null)
                  || parsed.data?.message 
                  || parsed.error 
                  || `Request failed with status ${parsed.status}`;
                
                // Log diagnostics for debugging (safe - no secrets)
                if (detail?.error_id) {
                  console.error('[Billing] Payment method error:', {
                    error_id: detail.error_id,
                    stage: detail.stage,
                    error: detail.error,
                    finix_code: detail.finix_code,
                    effective_environment: detail.effective_environment,
                  });
                }
                
                throw new Error(typeof errorMsg === "string" ? errorMsg : "Failed to save payment method.");
              }
              
              // Success - use backend-returned metadata (processor-authoritative)
              const saveResult = parsed.data;
              
              if (saveResult?.ok) {
                // TELEMETRY: subscription_activation_success
                console.log('[Billing] subscription_activation_success', {
                  tenant_id: tenantId,
                  timestamp: new Date().toISOString(),
                  deterministic_outcome: true,
                });
                
                toast.success("Payment method added successfully!");
                setAddCardModalOpen(false);
                setReplaceCardModalOpen(false);
                fetchData();
                fetchPaymentMethodSummary();
              } else {
                throw new Error(saveResult?.message || "Failed to save payment method.");
              }
              
            } catch (catchError) {
              // TELEMETRY: payment_submit_exception (catch-all for unexpected errors)
              console.error('[Billing] payment_submit_exception', {
                tenant_id: tenantId,
                timestamp: new Date().toISOString(),
                error_message: catchError?.message || 'Unknown error',
              });
              
              // Use friendly error mapper for user-facing messages
              const friendlyError = getFriendlyPaymentError(catchError?.message);
              setSubmitError(friendlyError);
              toast.error(friendlyError);
            } finally {
              // ALWAYS reset state to prevent stuck UI
              setFinixSubmitting(false);
              attachInProgressRef.current = false;
            }
          },
        }
      );
      
      finixPaymentFormInstance.current = form;
      
      // Fallback ready state after delay
      setTimeout(() => {
        if (finixPaymentFormInstance.current && !finixFormReady && finixTimeoutRef.current) {
          clearTimeout(finixTimeoutRef.current);
          finixTimeoutRef.current = null;
          setFinixFormReady(true);
        }
      }, 1500);
      
    } catch (error) {
      console.error("[Billing] Failed to initialize Finix form:", error);
      if (finixTimeoutRef.current) {
        clearTimeout(finixTimeoutRef.current);
        finixTimeoutRef.current = null;
      }
      setTokenizationError("Unable to initialize payment form. Please try again.");
    }
  }, [tokenizationConfig, finixFormReady, cleanupFinixForm, user, fetchData, fetchPaymentMethodSummary]);

  // Effect: Modal open/close handling
  useEffect(() => {
    if (!addCardModalOpen && !replaceCardModalOpen) {
      cleanupFinixForm();
      setTokenizationConfig(null);
      setTokenizationError(null);
      setSubmitError(null); // Clear submit errors when modal closes
      return;
    }
    
    if (tokenizationConfig && !tokenizationLoading && !finixFormReady && !tokenizationError) {
      const initTimer = setTimeout(() => initializeFinixForm(), 100);
      return () => clearTimeout(initTimer);
    }
  }, [addCardModalOpen, replaceCardModalOpen, tokenizationConfig, tokenizationLoading, finixFormReady, tokenizationError, initializeFinixForm, cleanupFinixForm]);

  // Effect: Fetch config when modal opens
  useEffect(() => {
    if ((addCardModalOpen || replaceCardModalOpen) && !tokenizationConfig && !tokenizationLoading) {
      fetchTokenizationConfig();
    }
  }, [addCardModalOpen, replaceCardModalOpen, tokenizationConfig, tokenizationLoading, fetchTokenizationConfig]);

  // Handle retry
  const handleRetryTokenization = useCallback(() => {
    cleanupFinixForm();
    setTokenizationConfig(null);
    setTokenizationError(null);
    setTokenizationLoading(false);
  }, [cleanupFinixForm]);

  // Fetch payment method summary on mount
  useEffect(() => {
    fetchPaymentMethodSummary();
  }, [fetchPaymentMethodSummary]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle size={14} className="mr-1" /> Success</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-800"><XCircle size={14} className="mr-1" /> Failed</Badge>;
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800"><Clock size={14} className="mr-1" /> Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading || registeringToken) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
        {registeringToken && (
          <p className="text-zinc-600">Registering your payment method...</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Billing & Payments</h1>
          <p className="text-zinc-600">Manage payment methods and view transaction history</p>
        </div>
      </div>

      {/* Mode Indicator - Only show in sandbox/test mode */}
      {modeInfo.mode === "test" && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-500 text-white">Sandbox Mode</Badge>
            <span className="text-sm text-amber-800">
              Payments are simulated and cards will not be charged.
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payment Methods Card */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard size={20} />
                Payment Methods
              </CardTitle>
              <CardDescription>
                Securely stored payment methods for your subscription
              </CardDescription>
            </div>
            <Button
              onClick={() => setAddCardModalOpen(true)}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="add-payment-method-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Card
            </Button>
          </CardHeader>
          <CardContent>
            {paymentMethods.length === 0 ? (
              <div className="text-center py-8" data-testid="no-payment-methods-empty-state">
                <div className="w-16 h-16 mx-auto mb-4 bg-zinc-100 rounded-full flex items-center justify-center">
                  <CreditCard size={32} className="text-zinc-400" />
                </div>
                <p className="text-lg font-medium text-zinc-700">No payment method on file</p>
                <p className="text-sm text-zinc-500 mt-1 mb-4">Add a card to enable automatic billing for premium features</p>
                <Button
                  onClick={() => setAddCardModalOpen(true)}
                  className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                  data-testid="empty-state-add-card-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Add Card
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentMethods.map((method) => (
                  <div
                    key={method.payment_method_id}
                    className={`p-4 border rounded-sm flex items-center justify-between ${
                      method.is_default ? "border-[#FF6600] bg-orange-50" : "border-zinc-200"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 bg-zinc-100 rounded flex items-center justify-center text-xs font-medium uppercase">
                        {method.brand || "Card"}
                      </div>
                      <div>
                        <p className="font-medium">•••• •••• •••• {method.last4 || '****'}</p>
                        <p className="text-sm text-zinc-500">
                          {method.exp_month && method.exp_year 
                            ? `Expires ${method.exp_month}/${method.exp_year}`
                            : 'Expiration not available'}
                        </p>
                      </div>
                      {method.is_default && (
                        <Badge className="bg-[#FF6600] text-white">
                          <Star size={12} className="mr-1" /> Default
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!method.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(method.payment_method_id)}
                        >
                          Set Default
                        </Button>
                      )}
                      
                      {/* REPLACE-NOT-DELETE POLICY:
                          - Actively billable tenants see "Replace" (opens add card modal)
                          - Free/non-billable tenants see "Remove" (trash icon)
                      */}
                      {billingPolicy.billing_required && paymentMethods.length === 1 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setReplaceCardModalOpen(true);
                            // Note: tokenization config is fetched automatically via useEffect when modal opens
                          }}
                          className="text-[#FF6600] border-[#FF6600] hover:bg-orange-50"
                          data-testid={`replace-payment-method-${method.payment_method_id}`}
                        >
                          <ArrowsCounterClockwise size={16} className="mr-1" />
                          Replace Card
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteModal(method.payment_method_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-payment-method-${method.payment_method_id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Billing Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarBlank size={20} />
              Billing Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {billingStatus ? (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-600">Status</span>
                  <Badge className={billingStatus.subscription_active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-800"}>
                    {billingStatus.subscription_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                {billingStatus.current_period_end && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-600">Next Billing</span>
                    <span className="font-medium">{formatDate(billingStatus.current_period_end)}</span>
                  </div>
                )}
                {billingStatus.monthly_total && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-600">Monthly Total</span>
                    <span className="font-medium text-lg">{billingStatus.monthly_total}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-center py-4">No active subscription</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Transactions Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Receipt size={20} />
              Transaction History
            </CardTitle>
            <CardDescription>
              {totalTransactions} total transactions
            </CardDescription>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <Receipt size={48} className="mx-auto mb-4 text-zinc-300" />
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div
                  key={tx.transaction_id}
                  className="p-4 border border-zinc-200 rounded-sm flex items-center justify-between hover:bg-zinc-50 cursor-pointer"
                  onClick={() => setTransactionDetailModal(tx)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.status === "success" ? "bg-emerald-100" : tx.status === "failed" ? "bg-red-100" : "bg-amber-100"
                    }`}>
                      {tx.status === "success" ? (
                        <CurrencyDollar size={20} className="text-emerald-600" />
                      ) : tx.status === "failed" ? (
                        <XCircle size={20} className="text-red-600" />
                      ) : (
                        <Clock size={20} className="text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{tx.description || "Payment"}</p>
                      <p className="text-sm text-zinc-500">{formatDate(tx.datetime)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-medium">{tx.amount_display}</p>
                      {getStatusBadge(tx.status)}
                    </div>
                    <CaretRight size={16} className="text-zinc-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Card Modal - Embedded Tokenization Form */}
      <Dialog open={addCardModalOpen} onOpenChange={setAddCardModalOpen}>
        <DialogContent className="sm:max-w-lg p-0">
          {/* Modal Header - Compact */}
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-100 space-y-1">
            <DialogTitle className="flex items-center gap-2.5 text-lg font-semibold">
              <div className="p-1.5 bg-emerald-50 rounded-full">
                <Lock size={18} className="text-emerald-600" />
              </div>
              Add Payment Method
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500 flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-emerald-600" />
              256-bit SSL encrypted • PCI DSS compliant
            </DialogDescription>
          </DialogHeader>
          
          {/* Modal Body - Scrollable */}
          <DialogBody className="px-6 py-4 space-y-4">
            {/* Loading state */}
            {tokenizationLoading && (
              <div className="flex flex-col items-center justify-center py-10">
                <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
                <span className="mt-3 text-zinc-600">Loading secure form...</span>
              </div>
            )}
            
            {/* Error state with Retry */}
            {tokenizationError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">Unable to Load Payment Form</p>
                    <p className="mt-1 text-sm text-red-700">{tokenizationError}</p>
                    <Button
                      onClick={handleRetryTokenization}
                      variant="outline"
                      size="sm"
                      className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                    >
                      <ArrowClockwise size={16} className="mr-2" />
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Finix Embedded Form */}
            {!tokenizationLoading && !tokenizationError && tokenizationConfig?.provider === "finix" && (
              <>
                {/* Submit/validation error - shown above form, form stays visible for retry */}
                {submitError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">{submitError}</p>
                        <p className="text-xs text-red-600 mt-1">Please correct the issue and try again.</p>
                        
                        {/* PREVIEW ONLY: Debug details collapsible */}
                        {isPreviewEnv && finixDebugError && (
                          <div className="mt-2 pt-2 border-t border-red-200">
                            <button
                              type="button"
                              onClick={() => setDebugExpanded(!debugExpanded)}
                              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-mono"
                            >
                              <Bug size={12} />
                              Debug details (preview only)
                              {debugExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
                            </button>
                            {debugExpanded && (
                              <div className="mt-2 p-2 bg-red-100 rounded text-xs font-mono text-red-900 overflow-x-auto">
                                <div><strong>Code:</strong> {finixDebugError.code || 'undefined'}</div>
                                <div><strong>Type:</strong> {finixDebugError.type || 'undefined'}</div>
                                <div><strong>Name:</strong> {finixDebugError.name || 'undefined'}</div>
                                <div><strong>Message:</strong> {finixDebugError.message || 'undefined'}</div>
                                <div><strong>Field:</strong> {finixDebugError.field || 'undefined'}</div>
                                {finixDebugError.details && (
                                  <div><strong>Details:</strong> {finixDebugError.details}</div>
                                )}
                                <div className="mt-1 pt-1 border-t border-red-200 text-red-600">
                                  <strong>Tenant:</strong> {finixDebugError.tenantId} | <strong>Time:</strong> {finixDebugError.ts}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Secure Card Entry info - compact */}
                <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <ShieldCheck size={16} className="text-emerald-600 flex-shrink-0" />
                    <span>Your card details are entered securely. We never see your full card number.</span>
                  </div>
                </div>
                
                {/* Country restriction notice */}
                <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <Info size={14} className="text-zinc-500" />
                    <span>Only United States and Canada are supported right now.</span>
                  </div>
                </div>
                
                {tokenizationConfig.environment === "sandbox" && (
                  <div className="p-2.5 bg-amber-50 border border-amber-100 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <Info size={14} className="text-amber-600" />
                      <span>Test mode is enabled.</span>
                    </div>
                  </div>
                )}
                
                {/* Form Container - Initializing State */}
                {!finixFormReady && (
                  <div className="flex flex-col items-center justify-center py-10 border border-zinc-200 rounded-lg bg-zinc-50">
                    <SpinnerGap size={24} className="animate-spin text-zinc-400" />
                    <span className="mt-2 text-sm text-zinc-500">Initializing secure form...</span>
                  </div>
                )}
                
                {/* Finix hosted form container */}
                <div 
                  ref={finixFormRef}
                  className={`min-h-[180px] border border-zinc-200 rounded-lg bg-white overflow-hidden ${!finixFormReady ? "hidden" : ""}`}
                  data-testid="finix-tokenization-form"
                  style={{ padding: "12px" }}
                />
              </>
            )}
            
            {/* Unsupported provider */}
            {!tokenizationLoading && !tokenizationError && tokenizationConfig && tokenizationConfig.provider !== "finix" && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Payment Setup Unavailable</p>
                    <p className="mt-1 text-sm text-red-700">Payment method setup is temporarily unavailable. Please contact support.</p>
                  </div>
                </div>
              </div>
            )}
          </DialogBody>
          
          {/* Modal Footer - Sticky */}
          <DialogFooter className="px-6 py-4 bg-zinc-50 border-t border-zinc-100 flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setAddCardModalOpen(false)}
              className="flex-1 h-10"
              disabled={finixSubmitting}
            >
              {finixSubmitting ? "Processing..." : "Cancel"}
            </Button>
            {/* External submit button REMOVED - Finix iframe submit is the single source of truth */}
            {finixSubmitting && (
              <div className="flex items-center text-sm text-zinc-500">
                <SpinnerGap size={16} className="animate-spin mr-2" />
                Saving payment method...
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Card Modal - Embedded Tokenization Form */}
      <Dialog open={replaceCardModalOpen} onOpenChange={setReplaceCardModalOpen}>
        <DialogContent className="sm:max-w-lg p-0">
          {/* Modal Header - Compact */}
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-100 space-y-1">
            <DialogTitle className="flex items-center gap-2.5 text-lg font-semibold">
              <div className="p-1.5 bg-zinc-100 rounded-full">
                <ArrowsClockwise size={18} className="text-zinc-600" />
              </div>
              Replace Payment Method
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500 flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-emerald-600" />
              256-bit SSL encrypted • PCI DSS compliant
            </DialogDescription>
          </DialogHeader>
          
          {/* Modal Body - Scrollable */}
          <DialogBody className="px-6 py-4 space-y-4">
            {/* Policy Note */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="text-amber-800">
                <strong>Active subscription detected.</strong> Adding a new card will replace your current payment method. 
                Your subscription will continue uninterrupted.
              </p>
            </div>
            
            {/* Loading state */}
            {tokenizationLoading && (
              <div className="flex flex-col items-center justify-center py-10">
                <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
                <span className="mt-3 text-zinc-600">Loading secure form...</span>
              </div>
            )}
            
            {/* Error state with Retry */}
            {tokenizationError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">Unable to Load Payment Form</p>
                    <p className="mt-1 text-sm text-red-700">{tokenizationError}</p>
                    <Button
                      onClick={handleRetryTokenization}
                      variant="outline"
                      size="sm"
                      className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                    >
                      <ArrowClockwise size={16} className="mr-2" />
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Finix Embedded Form */}
            {!tokenizationLoading && !tokenizationError && tokenizationConfig?.provider === "finix" && (
              <>
                {/* Submit/validation error - shown above form, form stays visible for retry */}
                {submitError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">{submitError}</p>
                        <p className="text-xs text-red-600 mt-1">Please correct the issue and try again.</p>
                        
                        {/* PREVIEW ONLY: Debug details collapsible */}
                        {isPreviewEnv && finixDebugError && (
                          <div className="mt-2 pt-2 border-t border-red-200">
                            <button
                              type="button"
                              onClick={() => setDebugExpanded(!debugExpanded)}
                              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-mono"
                            >
                              <Bug size={12} />
                              Debug details (preview only)
                              {debugExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
                            </button>
                            {debugExpanded && (
                              <div className="mt-2 p-2 bg-red-100 rounded text-xs font-mono text-red-900 overflow-x-auto">
                                <div><strong>Code:</strong> {finixDebugError.code || 'undefined'}</div>
                                <div><strong>Type:</strong> {finixDebugError.type || 'undefined'}</div>
                                <div><strong>Name:</strong> {finixDebugError.name || 'undefined'}</div>
                                <div><strong>Message:</strong> {finixDebugError.message || 'undefined'}</div>
                                <div><strong>Field:</strong> {finixDebugError.field || 'undefined'}</div>
                                {finixDebugError.details && (
                                  <div><strong>Details:</strong> {finixDebugError.details}</div>
                                )}
                                <div className="mt-1 pt-1 border-t border-red-200 text-red-600">
                                  <strong>Tenant:</strong> {finixDebugError.tenantId} | <strong>Time:</strong> {finixDebugError.ts}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Secure Card Entry info - compact */}
                <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <ShieldCheck size={16} className="text-emerald-600 flex-shrink-0" />
                    <span>Your new card details are entered securely.</span>
                  </div>
                </div>
                
                {/* Country restriction notice */}
                <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <Info size={14} className="text-zinc-500" />
                    <span>Only United States and Canada are supported right now.</span>
                  </div>
                </div>
                
                {/* Form Container - Initializing State */}
                {!finixFormReady && (
                  <div className="flex flex-col items-center justify-center py-10 border border-zinc-200 rounded-lg bg-zinc-50">
                    <SpinnerGap size={24} className="animate-spin text-zinc-400" />
                    <span className="mt-2 text-sm text-zinc-500">Initializing secure form...</span>
                  </div>
                )}
                
                {/* Finix hosted form container */}
                <div 
                  ref={finixFormRef}
                  className={`min-h-[180px] border border-zinc-200 rounded-lg bg-white overflow-hidden ${!finixFormReady ? "hidden" : ""}`}
                  data-testid="finix-tokenization-form-replace"
                  style={{ padding: "12px" }}
                />
              </>
            )}
            
            {/* Unsupported provider */}
            {!tokenizationLoading && !tokenizationError && tokenizationConfig && tokenizationConfig.provider !== "finix" && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Payment Setup Unavailable</p>
                    <p className="mt-1 text-sm text-red-700">Payment method setup is temporarily unavailable. Please contact support.</p>
                  </div>
                </div>
              </div>
            )}
          </DialogBody>
          
          {/* Modal Footer - Sticky */}
          <DialogFooter className="px-6 py-4 bg-zinc-50 border-t border-zinc-100 flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setReplaceCardModalOpen(false)}
              className="flex-1 h-10"
              disabled={finixSubmitting}
            >
              {finixSubmitting ? "Processing..." : "Cancel"}
            </Button>
            {/* External submit button REMOVED - Finix iframe submit is the single source of truth */}
            {finixSubmitting && (
              <div className="flex items-center text-sm text-zinc-500">
                <SpinnerGap size={16} className="animate-spin mr-2" />
                Saving payment method...
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction Detail Modal */}
      <Dialog open={!!transactionDetailModal} onOpenChange={() => setTransactionDetailModal(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          
          {transactionDetailModal && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-500">Transaction ID</p>
                  <p className="font-mono text-sm">{transactionDetailModal.transaction_id}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Status</p>
                  {getStatusBadge(transactionDetailModal.status)}
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Amount</p>
                  <p className="font-medium">{transactionDetailModal.amount_display}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Date</p>
                  <p>{formatDate(transactionDetailModal.datetime)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-zinc-500">Description</p>
                  <p>{transactionDetailModal.description || "N/A"}</p>
                </div>
                {transactionDetailModal.tc_transaction_id && (
                  <div className="col-span-2">
                    <p className="text-sm text-zinc-500">Transaction Reference</p>
                    <p className="font-mono text-sm">{transactionDetailModal.tc_transaction_id}</p>
                  </div>
                )}
                {transactionDetailModal.tc_response_message && transactionDetailModal.status === "failed" && (
                  <div className="col-span-2">
                    <p className="text-sm text-zinc-500">Failure Reason</p>
                    <p className="text-red-600">{transactionDetailModal.tc_response_message}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Payment Method Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={closeDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash size={20} />
              Remove Payment Method
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this payment method? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex-row gap-3 sm:justify-end">
            <Button
              variant="outline"
              onClick={closeDeleteModal}
              disabled={deleteLoading}
              className="flex-1 sm:flex-none"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePaymentMethod}
              disabled={deleteLoading}
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? (
                <>
                  <SpinnerGap size={16} className="animate-spin mr-2" />
                  Removing...
                </>
              ) : (
                "Remove"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
